"use client";

import { refreshTprWorkflowProgress } from "@/configuration/groups/tpr/components/workflow-progress";
import { useInvalidateServerComponent } from "@/src/components/server/client-provider";
import { Button } from "@/src/components/shadcn/ui/button";
import { toast } from "@/src/hooks/use-toast";
import {
  useContinueAfterManualTprStepMutation,
  useLocalTaskUpdateMutation,
  useUpsertTaskStepVerificationMutation,
} from "@/src/lib/data-fetchers/tasks-fetchers";
import { StepKey } from "@/src/lib/workflows/steps/types";
import { type Task, TaskStatus } from "@/src/models/task";
import { TaskStepPhase } from "@/src/models/task-step-card";
import { VerificationResult } from "@/src/models/task-verification";

/** A bypass retry is offered only while its completed checkpoint is still the latest step. */
const RUN_IN_PROGRESS_STATUSES: ReadonlySet<TaskStatus> = new Set([TaskStatus.PROCESSING, TaskStatus.PAUSED]);

export function TprManualStepContinueButton({
  task,
  stepKey,
  signOff,
  bypass = false,
}: {
  task: Task;
  stepKey: StepKey;
  bypass?: boolean;
  /**
   * The card to confirm once the row is settled. Pass it only when every step on the card has
   * already run, so nothing produced later is signed off unseen.
   */
  signOff?: { stepKey: string; workflowStepKeys: StepKey[] };
}) {
  const recheckEligibility = !bypass && stepKey === StepKey.SUBMIT_ATHENA_INSURANCE_ELIGIBILITY;
  const continueMutation = useContinueAfterManualTprStepMutation();
  const verificationMutation = useUpsertTaskStepVerificationMutation();
  const localTaskUpdateMutation = useLocalTaskUpdateMutation({ invalidateOnSettled: false });
  const invalidateInstructions = useInvalidateServerComponent("getReferralInstructions");

  const handleContinue = async () => {
    try {
      const { resumed } = await continueMutation.mutateAsync({ taskId: task.id, stepKey, bypass });

      if (signOff && !bypass) {
        // The row is already settled, so a failed sign-off only leaves the card to confirm by hand.
        try {
          await verificationMutation.mutateAsync({
            taskId: task.id,
            payload: {
              stepKey: signOff.stepKey,
              workflowStepKeys: signOff.workflowStepKeys,
              verdict: VerificationResult.CORRECT,
              // Ops is continuing a step that failed: the card is in its failed phase.
              phase: TaskStepPhase.FAILED,
            },
          });
        } catch (error) {
          console.error("Failed to record step verification after manual continue", error);
        }
      }

      if (resumed) {
        localTaskUpdateMutation.mutate({ ...task, status: TaskStatus.PROCESSING });
      }

      refreshTprWorkflowProgress(invalidateInstructions);
      toast({
        title: bypass
          ? "Manual override recorded"
          : recheckEligibility
            ? "Eligibility recheck started"
            : "Manual step confirmed",
        description: bypass
          ? "The workflow is continuing. Unverified results remain unverified."
          : !resumed
            ? "The workflow had already continued past this step."
            : recheckEligibility
              ? "The workflow will read and save Athena’s eligibility result before continuing."
              : "The workflow is continuing with the remaining steps.",
      });
    } catch (error) {
      toast({
        title: "Unable to continue the workflow",
        description: error instanceof Error ? error.message : "Refresh the task and try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Button
      size="sm"
      variant={bypass ? "outline" : "default"}
      onClick={handleContinue}
      disabled={
        (RUN_IN_PROGRESS_STATUSES.has(task.status) && !bypass) ||
        continueMutation.isPending ||
        verificationMutation.isPending ||
        localTaskUpdateMutation.isPending
      }
    >
      {continueMutation.isPending
        ? "Continuing..."
        : bypass
          ? "Continue anyway"
          : recheckEligibility
            ? "Recheck eligibility"
            : "Continue"}
    </Button>
  );
}
